import { useMemo, useState } from 'react';

export interface TreeNode<TMeta = unknown> {
  id: string;
  label: string;
  count?: number;
  meta?: TMeta;
  children?: TreeNode<TMeta>[];
}

interface TreeViewProps<TMeta = unknown> {
  root: TreeNode<TMeta>;
  className?: string;
  defaultExpandedDepth?: number;
}

function makeDefaultExpanded(root: TreeNode, depth: number): Set<string> {
  const expanded = new Set<string>();
  function visit(node: TreeNode, d: number) {
    if (d <= 0) return;
    expanded.add(node.id);
    node.children?.forEach((c) => visit(c, d - 1));
  }
  visit(root, depth);
  return expanded;
}

export function TreeView<TMeta = unknown>({
  root,
  className = '',
  defaultExpandedDepth = 3,
}: TreeViewProps<TMeta>) {
  const initialExpanded = useMemo(
    () => makeDefaultExpanded(root, defaultExpandedDepth),
    [root, defaultExpandedDepth]
  );
  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set<string>();
    const walk = (n: TreeNode) => {
      all.add(n.id);
      n.children?.forEach(walk);
    };
    walk(root);
    setExpanded(all);
  };

  const collapseAll = () => setExpanded(new Set([root.id]));

  return (
    <div className={className}>
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={expandAll}
          className="px-2 py-1 text-xs font-medium rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="px-2 py-1 text-xs font-medium rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
        >
          Collapse all
        </button>
      </div>

      <div className="border border-gray-200 rounded-lg bg-white p-2">
        <TreeNodeRow node={root} expanded={expanded} onToggle={toggle} depth={0} />
      </div>
    </div>
  );
}

interface TreeNodeRowProps {
  node: TreeNode;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  depth: number;
}

function TreeNodeRow({ node, expanded, onToggle, depth }: TreeNodeRowProps) {
  const hasChildren = Boolean(node.children && node.children.length > 0);
  const isOpen = expanded.has(node.id);

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50"
        style={{ paddingLeft: `${depth * 14 + 6}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="w-5 h-5 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-700"
            aria-label={isOpen ? 'Collapse node' : 'Expand node'}
          >
            {isOpen ? '▾' : '▸'}
          </button>
        ) : (
          <span className="w-5 inline-block text-gray-300 select-none">•</span>
        )}

        <span className="text-sm text-gray-900">
          {node.label}
          {typeof node.count === 'number' ? (
            <span className="text-xs text-gray-500 ml-2">({node.count})</span>
          ) : null}
        </span>
      </div>

      {hasChildren && isOpen && (
        <div>
          {node.children!.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

